import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, fmtNum, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, ClipboardTextIcon, PlayIcon, CheckCircleIcon,
  WarehouseIcon, EyeIcon, XIcon, FloppyDiskIcon, PrinterIcon,
  LightbulbIcon
} from "@phosphor-icons/react";
import BarcodeScanInput from "@/components/BarcodeScanInput";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { normalizar } from "@/components/LiveSearch";

/**
 * Conteo Físico de Inventario — Módulo 1.4
 *
 * Flujo:
 *  1. Crear conteo (snapshot del stock teórico)
 *  2. Imprimir hoja → ir a la bodega → registrar contado
 *  3. Finalizar → genera ajustes automáticamente
 */
const ESTADOS = {
  borrador: { label: "Borrador", color: "bg-amber-500/20 text-amber-400 border-amber-500/40" },
  en_progreso: { label: "En progreso", color: "bg-blue-500/20 text-blue-400 border-blue-500/40" },
  completado: { label: "Completado", color: "bg-agro-green/20 text-agro-green border-agro-green/40" },
  cancelado: { label: "Cancelado", color: "bg-text-muted/20 text-text-muted border-text-muted/40" },
};

export default function InventoryCounts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [viewing, setViewing] = useState(null);  // ID del conteo a ver

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory-counts");
      setItems(data.items || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="inventory-counts-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <ClipboardTextIcon size={22} weight="duotone" className="text-agro-yellow" />
              <h1 className="text-2xl font-heading font-bold">Conteo Físico</h1>
            </div>
            <p className="text-sm text-text-muted">Compara stock teórico vs real y genera ajustes automáticos.</p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-count-btn"
            className="inline-flex items-center gap-2 bg-agro-green hover:bg-agro-green-hover text-black font-medium px-4 py-2 rounded-md text-sm"
          >
            <PlusIcon size={14} weight="bold" /> Nuevo conteo
          </button>
        </div>

        {/* Tabla de conteos */}
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Código</th>
                <th className="p-3">Bodega</th>
                <th className="p-3">Tipo</th>
                <th className="p-3">Programado</th>
                <th className="p-3 text-center">Avance</th>
                <th className="p-3 text-right">Diferencia (L)</th>
                <th className="p-3 text-center">Estado</th>
                <th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-10 text-center text-text-muted">Cargando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="8" className="p-10 text-center text-text-muted">
                  No hay conteos físicos. Crea uno para empezar.
                </td></tr>
              ) : items.map((c) => {
                const est = ESTADOS[c.estado] || ESTADOS.borrador;
                const avance = c.total_lineas > 0 ? Math.round((c.lineas_contadas / c.total_lineas) * 100) : 0;
                return (
                  <tr key={c.id} className="border-t border-border/40 hover:bg-app/40" data-testid={`count-row-${c.id}`}>
                    <td className="p-3 pl-5 font-mono text-xs">{c.codigo}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <WarehouseIcon size={14} className="text-text-muted" />
                        <span>{c.bodega_nombre}</span>
                      </div>
                    </td>
                    <td className="p-3 text-xs">
                      {c.tipo === "total" ? "Total" : c.tipo === "categoria" ? `Categoría: ${c.categoria_filtro}` : "Lista personalizada"}
                    </td>
                    <td className="p-3 text-xs">{fmtDate(c.fecha_programada)}</td>
                    <td className="p-3 text-center text-xs">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 bg-app rounded-full h-1.5 overflow-hidden">
                          <div className="h-full bg-agro-green" style={{ width: `${avance}%` }} />
                        </div>
                        <span className="text-text-muted">{c.lineas_contadas}/{c.total_lineas}</span>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <span className={c.diferencia_total_valor < 0 ? "text-red-400" : c.diferencia_total_valor > 0 ? "text-agro-yellow" : ""}>
                        {fmtL(c.diferencia_total_valor)}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${est.color}`}>
                        {est.label}
                      </span>
                    </td>
                    <td className="p-3 pr-5 text-right">
                      <button
                        onClick={() => setViewing(c.id)}
                        data-testid={`view-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-border rounded-md hover:border-agro-green/40 hover:bg-white/5"
                      >
                        <EyeIcon size={12} /> Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewCountModal onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); setViewing(id); load(); }} />}
      {viewing && <CountDetailModal cid={viewing} onClose={() => { setViewing(null); load(); }} />}
    </Layout>
  );
}

// ============================================================
// MODAL: Nuevo conteo
// ============================================================
function NewCountModal({ onClose, onCreated }) {
  const [bodegas, setBodegas] = useState([]);
  const [form, setForm] = useState({
    bodega_id: "", tipo: "total", categoria_filtro: "",
    fecha_programada: new Date().toISOString().slice(0, 10),
    responsable_nombre: "", notas: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/warehouses").then(({ data }) => {
      const list = Array.isArray(data) ? data : data.items || [];
      setBodegas(list);
      if (list.length && !form.bodega_id) {
        setForm((f) => ({ ...f, bodega_id: list[0].id }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bodega_id) { toast.error("Selecciona una bodega"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/inventory-counts", form);
      toast.success(`Conteo ${data.codigo} creado (${data.total_lineas} productos)`);
      onCreated(data.id);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="new-count-modal" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-xl w-full max-w-xl">
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <h2 className="font-semibold flex items-center gap-2"><PlusIcon size={16} /> Nuevo conteo físico</h2>
          <button aria-label="Cerrar" onClick={m.cerrar}><XIcon size={16} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Bodega *</label>
            <select
              value={form.bodega_id}
              onChange={(e) => setForm({ ...form, bodega_id: e.target.value })}
              required
              data-testid="bodega-select"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            >
              <option value="">— Selecciona —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.codigo} — {b.nombre}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Tipo de conteo</label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                data-testid="tipo-select"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="total">Total (todos los productos)</option>
                <option value="categoria">Por categoría</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Fecha programada</label>
              <input
                type="date"
                value={form.fecha_programada}
                onChange={(e) => setForm({ ...form, fecha_programada: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>
          {form.tipo === "categoria" && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Categoría a contar</label>
              <input
                value={form.categoria_filtro}
                onChange={(e) => setForm({ ...form, categoria_filtro: e.target.value })}
                placeholder="Ej: Fertilizantes"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Responsable</label>
            <input
              value={form.responsable_nombre}
              onChange={(e) => setForm({ ...form, responsable_nombre: e.target.value })}
              placeholder="Quien hará el conteo"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1">Notas</label>
            <textarea
              value={form.notas}
              onChange={(e) => setForm({ ...form, notas: e.target.value })}
              rows="2"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
            <button type="button" onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
            <button type="submit" disabled={(saving) || m.saving} data-testid="create-count-btn"
              className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md disabled:opacity-60">
              {saving ? "Creando…" : "Crear conteo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// MODAL: Detalle del conteo (con líneas editables)
// ============================================================
function CountDetailModal({ cid, onClose }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [cargas, setCargas] = useState(0);         // versión de los datos: al recargar tras guardar, «sin cambios»

  const load = async () => {
    try {
      const { data } = await api.get(`/inventory-counts/${cid}`);
      setC(data); setCargas((n) => n + 1);
    } catch (e) { toast.error(apiErr(e)); onClose(); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cid]);

  const updateLine = (idx, value) => {
    if (!c) return;
    const v = value === "" ? null : parseFloat(value);
    const newLineas = [...c.lineas];
    newLineas[idx] = {
      ...newLineas[idx],
      stock_contado: v,
      diferencia: v === null ? 0 : v - newLineas[idx].stock_teorico,
      valor_diferencia: v === null ? 0 : (v - newLineas[idx].stock_teorico) * (newLineas[idx].costo_unitario || 0),
    };
    setC({ ...c, lineas: newLineas });
  };

  const saveAll = async () => {
    if (!c) return;
    setSaving(true);
    try {
      const linesToUpdate = c.lineas
        .filter((ln) => ln.stock_contado !== null && ln.stock_contado !== undefined)
        .map((ln) => ({ line_id: ln.id, stock_contado: ln.stock_contado, notas: ln.notas || "" }));
      await api.post(`/inventory-counts/${cid}/bulk`, { lines: linesToUpdate });
      toast.success("Conteo guardado");
      await load();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  // Lector de código de barras: cada escaneo suma 1 al contado de la línea (agrega la línea si no existe)
  const onScan = async (p) => {
    if (!c) return;
    let idx = c.lineas.findIndex((x) => x.producto_id === p.id);
    let lineas = c.lineas;
    if (idx < 0) {
      try {
        const { data } = await api.post(`/inventory-counts/${cid}/lines`, { producto_id: p.id });
        lineas = [...c.lineas, data.line];
        idx = lineas.length - 1;
        toast.info(`${p.codigo} agregado al conteo`);
      } catch (er) { toast.error(apiErr(er)); return; }
    }
    const ln = lineas[idx];
    const v = Number(ln.stock_contado || 0) + 1;
    const nl = [...lineas];
    nl[idx] = { ...ln, stock_contado: v, diferencia: v - ln.stock_teorico, valor_diferencia: (v - ln.stock_teorico) * (ln.costo_unitario || 0) };
    setC({ ...c, lineas: nl, total_lineas: nl.length });
    setSearch(p.codigo);
  };

  // Hoja de conteo imprimible por bodega (sistema vs conteo)
  const printSheet = () => {
    if (!c) return;
    const esc = (t) => String(t ?? "").replace(/[&<>]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[ch]));
    const rows = (c.lineas || []).map((ln) => `<tr><td>${esc(ln.producto_codigo)}</td><td>${esc(ln.producto_nombre)}</td><td>${esc(ln.unidad)}</td><td>${esc(ln.ubicacion)}</td><td class="n">${fmtNum(ln.stock_teorico, 2)}</td><td class="n">${ln.stock_contado === null || ln.stock_contado === undefined ? "" : fmtNum(ln.stock_contado, 2)}</td><td class="n">${ln.stock_contado === null || ln.stock_contado === undefined ? "" : fmtNum(ln.diferencia, 2)}</td><td></td></tr>`).join("");
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permite ventanas emergentes para imprimir"); return; }
    w.document.write(`<html><head><title>Hoja de conteo ${esc(c.codigo)}</title><style>
      body{font-family:Arial,sans-serif;font-size:11px;margin:20px} h1{font-size:16px;margin:0} table{width:100%;border-collapse:collapse;margin-top:12px}
      th,td{border:1px solid #999;padding:4px 6px} th{background:#eee;text-align:left} .n{text-align:right} .meta{color:#555;margin-top:4px}
      .firmas{display:flex;gap:60px;margin-top:40px} .firmas div{border-top:1px solid #000;padding-top:4px;width:220px;text-align:center}
    </style></head><body>
      <h1>Hoja de conteo físico ${esc(c.codigo)}</h1>
      <div class="meta">Bodega: <b>${esc(c.bodega_codigo)} — ${esc(c.bodega_nombre)}</b> · Tipo: ${esc(c.tipo)}${c.categoria_filtro ? " (" + esc(c.categoria_filtro) + ")" : ""} · Fecha: ${esc(fmtDate(c.fecha_programada))} · Responsable: ${esc(c.responsable_nombre || "________________")}</div>
      <table><thead><tr><th>Código</th><th>Producto</th><th>Unidad</th><th>Ubicación</th><th class="n">Sistema</th><th class="n">Conteo</th><th class="n">Diferencia</th><th>Observaciones</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="firmas"><div>Contado por</div><div>Revisado por</div></div>
      <script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  const startCount = async () => {
    try { await api.post(`/inventory-counts/${cid}/start`); toast.success("Conteo iniciado"); load(); }
    catch (er) { toast.error(apiErr(er)); }
  };

  const finalize = async () => {
    if (!window.confirm("¿Finalizar el conteo? Se generarán los ajustes de inventario y NO podrás editarlo más.")) return;
    try {
      const { data } = await api.post(`/inventory-counts/${cid}/finalize`);
      toast.success(`Conteo finalizado. ${data.ajustes_generados} ajuste(s) generado(s).`);
      load();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: c ? c.lineas.map((l) => [l.stock_contado, l.notas || ""]) : undefined, listo: cargas, onClose: onClose, onSave: saveAll });
  if (!c) return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" {...m.overlayProps}>
      <div className="text-text-muted">Cargando conteo…</div>
    </div>
  );

  const est = ESTADOS[c.estado] || ESTADOS.borrador;
  const editable = c.estado === "borrador" || c.estado === "en_progreso";
  const avance = c.total_lineas > 0 ? Math.round((c.lineas_contadas / c.total_lineas) * 100) : 0;

  const palabras = normalizar(search).split(/\s+/).filter(Boolean);
  const filtered = (c.lineas || []).filter((ln) => {
    const t = normalizar(`${ln.producto_codigo || ""} ${ln.producto_nombre || ""} ${ln.sku || ""} ${ln.lote || ""}`);
    return palabras.every((w) => t.includes(w));
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="count-detail-modal" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        {/* HEADER */}
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="font-bold text-lg flex items-center gap-2">
                <ClipboardTextIcon size={18} weight="duotone" className="text-agro-yellow" />
                {c.codigo}
              </h2>
              <div className="text-xs text-text-muted">Bodega: {c.bodega_nombre} · {c.tipo}</div>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${est.color}`}>{est.label}</span>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar}><XIcon size={18} /></button>
        </div>

        {/* RESUMEN */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-app/40">
          <Metric label="Líneas totales" value={c.total_lineas} />
          <Metric label="Contadas" value={`${c.lineas_contadas} (${avance}%)`} />
          <Metric label="Diferencia unidades" value={fmtNum(c.diferencia_total_unidades, 2)}
            color={c.diferencia_total_unidades < 0 ? "text-red-400" : "text-agro-green"} />
          <Metric label="Diferencia valor" value={fmtL(c.diferencia_total_valor)}
            color={c.diferencia_total_valor < 0 ? "text-red-400" : "text-agro-green"} />
        </div>

        {/* SEARCH */}
        <div className="p-3 border-b border-border/40 grid grid-cols-1 md:grid-cols-2 gap-2">
          {editable && <BarcodeScanInput testid="count-barcode" onFound={onScan} placeholder="Escanear (cada lectura suma 1 al contado)…" />}
          <div>
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Buscar en el conteo"
              placeholder="Buscar por código, nombre, SKU o lote…" data-testid="count-search"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            {palabras.length > 0 && <div className="text-[11px] text-text-muted mt-1" aria-live="polite">
              {filtered.length ? `${filtered.length} de ${(c.lineas || []).length} coincidencias` : "Sin resultados"}</div>}
          </div>
        </div>

        {/* TABLA DE LÍNEAS */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Código</th>
                <th className="p-3">Producto</th>
                <th className="p-3">Ubicación</th>
                <th className="p-3 text-right">Teórico</th>
                <th className="p-3 text-right w-[140px]">Contado</th>
                <th className="p-3 text-right">Diferencia</th>
                <th className="p-3 text-right">Valor Dif.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ln, idx) => {
                const realIdx = c.lineas.findIndex((x) => x.id === ln.id);
                const dif = ln.stock_contado === null || ln.stock_contado === undefined ? null : ln.diferencia;
                return (
                  <tr key={ln.id} className="border-t border-border/40">
                    <td className="p-2 pl-5 font-mono text-xs">{ln.producto_codigo}</td>
                    <td className="p-2 text-xs">{ln.producto_nombre} <span className="text-text-muted">{ln.unidad}</span></td>
                    <td className="p-2 text-[11px] text-text-muted">{ln.ubicacion || "—"}</td>
                    <td className="p-2 text-right text-xs">{fmtNum(ln.stock_teorico, 2)}</td>
                    <td className="p-1">
                      <input
                        type="number"
                        step="0.01"
                        value={ln.stock_contado === null || ln.stock_contado === undefined ? "" : ln.stock_contado}
                        onChange={(e) => updateLine(realIdx, e.target.value)}
                        disabled={!editable}
                        data-testid={`count-line-${ln.id}`}
                        placeholder="—"
                        className="w-full bg-app border border-border rounded px-2 py-1 text-right text-xs focus:border-agro-green focus:outline-none disabled:opacity-50"
                      />
                    </td>
                    <td className={`p-2 text-right text-xs font-medium ${dif === null ? "" : dif < 0 ? "text-red-400" : dif > 0 ? "text-agro-yellow" : "text-text-muted"}`}>
                      {dif === null ? "—" : (dif > 0 ? "+" : "") + fmtNum(dif, 2)}
                    </td>
                    <td className={`p-2 text-right text-xs ${dif === null ? "" : dif < 0 ? "text-red-400" : dif > 0 ? "text-agro-green" : "text-text-muted"}`}>
                      {dif === null ? "—" : fmtL(ln.valor_diferencia)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="p-4 border-t border-border/60 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-text-muted">
            {editable ? (
              <><LightbulbIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Deja vacío lo que no contaste · finalizar generará ajustes automáticos.</>
            ) : c.estado === "completado" && c.ajuste_ids?.length > 0 ? (
              <><CheckCircleIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Generó <strong>{c.ajuste_ids.length}</strong> ajuste(s) de inventario.</>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={printSheet} data-testid="print-sheet-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary">
              <PrinterIcon size={12} weight="bold" /> Imprimir hoja de conteo
            </button>
            {c.estado === "borrador" && (
              <button onClick={startCount} data-testid="start-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-blue-500/40 bg-blue-500/15 text-blue-400 rounded-md">
                <PlayIcon size={12} weight="bold" /> Iniciar
              </button>
            )}
            {editable && (
              <button onClick={m.guardar} disabled={(saving) || m.saving} data-testid="save-all-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-agro-yellow/40 bg-agro-yellow/15 text-agro-yellow rounded-md disabled:opacity-60">
                <FloppyDiskIcon size={12} weight="bold" /> {saving ? "Guardando…" : "Guardar avance"}
              </button>
            )}
            {c.estado === "en_progreso" && (
              <button onClick={finalize} data-testid="finalize-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-agro-green text-black font-medium rounded-md">
                <CheckCircleIcon size={12} weight="bold" /> Finalizar y generar ajustes
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color = "" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
